import { Check, Copy } from 'lucide-react';
import { useCopyToClipboard } from '@/shared/hooks/useCopyToClipboard';
import { cn } from '@/shared/lib/cn';
import {
  isGuestCredentialLink,
  normalizeMessageBody,
  shortenUrl,
  splitMessageLinks,
} from './messageText';

/**
 * Der Text einer Nachricht: Umbrüche auf Lesefluss gezogen, Adressen als echte Links.
 *
 * `whitespace-pre-wrap` bleibt, weil `normalizeMessageBody` die Absätze bewusst als Leerzeile
 * stehen lässt — nur die weichen Umbrüche der Mailformatierung sind verschwunden.
 */
export function MessageBody({
  body,
  onDark,
  className,
}: {
  body: string;
  onDark?: boolean;
  className?: string;
}) {
  const segments = splitMessageLinks(normalizeMessageBody(body));
  return (
    <p className={cn('whitespace-pre-wrap', className)}>
      {segments.map((segment, index) => {
        if (segment.type === 'text') return <span key={index}>{segment.value}</span>;
        if (isGuestCredentialLink(segment.value)) {
          return <GuestLink key={index} url={segment.value} onDark={onDark} />;
        }
        return (
          <a
            key={index}
            href={segment.value}
            title={segment.value}
            target="_blank"
            // `noopener` trennt das geöffnete Fenster von unserem `window`, `noreferrer` hält die
            // Adresse aus dem Referrer des Ziels heraus.
            rel="noopener noreferrer"
            className={cn('underline underline-offset-2', linkTone(onDark))}
          >
            {shortenUrl(segment.value)}
          </a>
        );
      })}
    </p>
  );
}

function linkTone(onDark?: boolean) {
  return onDark ? 'text-[#F1EAD4] decoration-[#F1EAD4]/50' : 'text-postmark';
}

/**
 * Ein Gastzugang wird kopiert, nicht geöffnet.
 *
 * Der Abruf zählt serverseitig als Verwendung durch den Gast — er nimmt ihm eine seiner
 * Verwendungen und stempelt seinen Link im Dashboard und in der Onboarding-Liste auf
 * „Verwendet". Deshalb ist dieser Knopf kein Link: er legt die vollständige Adresse in die
 * Zwischenablage, damit man sie dem Gast weiterreichen kann.
 */
function GuestLink({ url, onDark }: { url: string; onDark?: boolean }) {
  const [copied, copy] = useCopyToClipboard();
  return (
    <button
      type="button"
      onClick={() => void copy(url)}
      title={url}
      aria-label={`Gastzugang kopieren: ${shortenUrl(url)}. Öffnen würde ihn als vom Gast verwendet zählen.`}
      className={cn(
        'inline-flex min-h-11 items-center gap-1.5 align-middle underline decoration-dotted underline-offset-2 sm:min-h-0',
        linkTone(onDark),
      )}
    >
      {copied ? <Check size={13} aria-hidden /> : <Copy size={13} aria-hidden />}
      {copied ? 'Gastzugang kopiert' : shortenUrl(url)}
    </button>
  );
}
